import { useState } from 'react';
import { addDays, dayStart, sameDay } from '../lib/calendar-day';
import { formatWhen } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { Button, Label } from './ui';

/** The hours a car is usually promised for; anything else is typed in. */
const HOURS = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];

const pad = (n: number) => String(n).padStart(2, '0');
const toDateInput = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/**
 * Picking when a car will be ready: a day from the next three or the calendar,
 * an hour from the usual ones or typed — big targets, built for a thumb.
 * Reports the moment once both a day and a time are chosen.
 */
export function DuePicker({ value, onChange }: { value: Date | null; onChange: (due: Date | null) => void }) {
  const { t } = useI18n();
  const today = dayStart(new Date());
  const [day, setDay] = useState(() => dayStart(value ?? today));
  const [time, setTime] = useState(() => (value ? `${pad(value.getHours())}:${pad(value.getMinutes())}` : ''));
  const [ownTime, setOwnTime] = useState(() => time !== '' && !HOURS.some((h) => time === `${pad(h)}:00`));
  // No day is lit until there is a deadline to show, or someone has picked one.
  const [dayPicked, setDayPicked] = useState(value !== null);
  const quickDays = [0, 1, 2].map((n) => addDays(today, n));
  const otherDay = dayPicked && !quickDays.some((d) => sameDay(d, day));
  const dayNames = [t('due.today'), t('due.tomorrow'), t('due.afterTomorrow')];

  const report = (nextDay: Date, nextTime: string) => {
    if (!nextTime) { onChange(null); return; }
    const [hours, minutes] = nextTime.split(':').map(Number);
    onChange(new Date(nextDay.getFullYear(), nextDay.getMonth(), nextDay.getDate(), hours, minutes));
  };
  const pickDay = (d: Date) => { setDay(d); setDayPicked(true); report(d, time); };
  const pickTime = (value: string, own: boolean) => {
    setOwnTime(own); setTime(value); setDayPicked(true); report(day, value);
  };

  const option = (active: boolean) => `min-h-12 rounded-xl border text-sm font-semibold transition ${
    active ? 'border-brand bg-brand-wash text-brand' : 'border-line bg-raised text-ink hover:border-ink-faint'
  }`;

  return (
    <div className="space-y-4">
      <div>
        <Label>{t('due.day')}</Label>
        <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {quickDays.map((d, i) => (
            <button key={i} type="button" className={option(dayPicked && !otherDay && sameDay(d, day))} onClick={() => pickDay(d)}>
              {dayNames[i]}
            </button>
          ))}
          <label className={`${option(otherDay)} relative flex items-center justify-center`}>
            {otherDay
              ? new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' }).format(day).replace('.', '')
              : t('due.otherDay')}
            <input
              type="date"
              min={toDateInput(today)}
              value={toDateInput(day)}
              onChange={(e) => { if (e.target.value) pickDay(dayStart(new Date(`${e.target.value}T00:00`))); }}
              className="absolute inset-0 cursor-pointer opacity-0"
              aria-label={t('due.otherDay')}
            />
          </label>
        </div>
      </div>

      <div>
        <Label>{t('due.time')}</Label>
        <div className="mt-1.5 grid grid-cols-4 gap-2">
          {HOURS.map((h) => {
            const hour = `${pad(h)}:00`;
            return (
              <button
                key={h}
                type="button"
                className={`tabular ${option(!ownTime && time === hour)}`}
                onClick={() => pickTime(hour, false)}
              >
                {hour}
              </button>
            );
          })}
        </div>
        <label className={`tabular mt-2 flex min-h-12 items-center justify-between gap-3 rounded-xl border px-4 text-sm font-semibold ${
          ownTime ? 'border-brand bg-brand-wash text-brand' : 'border-line bg-raised text-ink'
        }`}>
          {t('due.otherTime')}
          <input
            type="time"
            step={900}
            value={ownTime ? time : ''}
            onChange={(e) => pickTime(e.target.value, true)}
            className="tabular bg-transparent text-right text-base text-ink outline-none"
          />
        </label>
      </div>
    </div>
  );
}

/** The picker with its buttons, for moving the time on a car already on the floor. */
export function DueEditor({ initial, saving, clearing, onSave, onClear }: {
  initial: Date | null;
  saving: boolean;
  clearing: boolean;
  onSave: (due: Date) => void;
  onClear: (() => void) | null;
}) {
  const { t } = useI18n();
  const [due, setDue] = useState<Date | null>(initial);
  return (
    <div className="space-y-4">
      <DuePicker value={initial} onChange={setDue} />
      <Button size="lg" className="w-full" disabled={!due} loading={saving} onClick={() => due && onSave(due)}>
        {due ? `${t('common.save')} — ${formatWhen(due)}` : t('due.pickTime')}
      </Button>
      {onClear && (
        <Button variant="quiet" className="w-full" loading={clearing} onClick={onClear}>{t('floor.clearPromise')}</Button>
      )}
    </div>
  );
}
